import { useCallback } from 'react';
import isNil from 'lodash/isNil';
import { useMutation } from '@apollo/client';
import {
  IDashboardFilterInput,
  IDashboardSetFilterMutation,
  IDashboardSetFilterMutationVariables,
} from '../../../../../../utils/apollo/graphqlSchema';
import { dashboardSetFilterMutation } from '../../../../../../utils/apollo/dashboards';
import { assertNotNil } from '../../../../../../utils/types';
import printGraphqlErrors from '../../../../../../utils/apollo/printGraphqlErrors';
import { dashboardFilterUpdated } from '../../../../../../utils/analytics/events/dashboard/events';
import toastMessage from '../../../../../../utils/toastMessage';
import prepareDashboardFilterInput from './prepareDashboardFilterInput';
import {
  closeModal,
  ModalSliceType,
  openModal,
} from '../../../../../../redux/slices/modal';
import { useAppDispatch } from '../../../../../../redux/hooks';
import useDashboardSelector from '../../../../../../redux/selectorHooks/useDashboardSelector';
import useDashboardSchemaId from '../../../../utils/useDashboardSchemaId';
import useDashboardReload from '../../../../utils/useDashboardReload';
import { useAnalytics } from '../../../../../../utils/analytics/useAnalytics';
import useUpdateDashboardStore from '../../../../utils/useUpdateDashboardStore';

const useSetDashboardFilter = () => {
  const dispatch = useAppDispatch();
  const updateDashboardStore = useUpdateDashboardStore();
  const { track } = useAnalytics();

  const dashboard = useDashboardSelector('dashboard');
  assertNotNil(dashboard);
  const { baseId, id, name } = dashboard;

  const schemaId = useDashboardSchemaId();
  const draftCustomization = useDashboardSelector('draftCustomization');
  const reloadDashboard = useDashboardReload(dashboard.baseId);

  const [executeDashboardSetFilter] = useMutation<
    IDashboardSetFilterMutation,
    IDashboardSetFilterMutationVariables
  >(dashboardSetFilterMutation);

  const updateDashboardFilter = useCallback(
    async (filter: IDashboardFilterInput | null) => {
      const { data } = await executeDashboardSetFilter({
        variables: {
          input: {
            baseId,
            instanceId: id,
            filter,
          },
        },
      });
      assertNotNil(data);
      const operation = data.dashboardSetFilter;
      const { errors, entity: updatedDashboard } = operation;
      if (printGraphqlErrors(errors)) {
        await reloadDashboard();
        return;
      }
      assertNotNil(updatedDashboard);

      updateDashboardStore({
        dashboard: updatedDashboard,
        retainAnalyses: true,
      });
      track(dashboardFilterUpdated(updatedDashboard));

      if (isNil(filter)) {
        toastMessage({
          message: 'Dashboard filters reset.',
          type: 'info',
        });
      }
    },
    [
      baseId,
      executeDashboardSetFilter,
      id,
      reloadDashboard,
      track,
      updateDashboardStore,
    ],
  );

  const handleSaveCustomizationsAsFilter = useCallback(
    () =>
      updateDashboardFilter(
        prepareDashboardFilterInput({
          draftCustomization,
          schemaId,
        }),
      ),
    [draftCustomization, schemaId, updateDashboardFilter],
  );

  const handleRemoveDashboardFilter = useCallback(
    () =>
      dispatch(
        openModal({
          type: ModalSliceType.DashboardRemoveFilter,
          data: {
            dashboardName: name,
            onRemove: async () => {
              await updateDashboardFilter(null);
              dispatch(closeModal());
            },
          },
        }),
      ),
    [name, dispatch, updateDashboardFilter],
  );

  return {
    handleSaveCustomizationsAsFilter,
    handleRemoveDashboardFilter,
  };
};

export default useSetDashboardFilter;
